// ============================================
// MIDIA.JS — Pantalla personalizada "Mi día"
// Contenido según rol del usuario
// ============================================

import React, { useState, useEffect } from 'react';
import PipelineCotizaciones from './components/PipelineCotizaciones';
import ReporteMatutino from './components/ReporteMatutino';
import MiBandeja from './components/MiBandeja';
import AvisoSincronizacionSyscom from './components/AvisoSincronizacionSyscom';
import TarjetaVigilante from './components/TarjetaVigilante';
import TarjetaAvisosExternos from './components/TarjetaAvisosExternos';
import { useUsuario } from './context/UsuarioContext';
import { authHeaders } from './utils/storage';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { API_BASE } from './config/api';
import { OPC_DINERO } from './utils/dinero';

const PP = "'Poppins', sans-serif";
const colores = { principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#5e666e', amarillo: '#dcde74', verde: '#5cd4b0', rojo: '#e05c5c', fondo: '#f3f4f4' };

const graficaSemana = [
  { dia: 'Lun', ingresos: 18500 },
  { dia: 'Mar', ingresos: 32000 },
  { dia: 'Mié', ingresos: 12000 },
  { dia: 'Jue', ingresos: 45000 },
  { dia: 'Vie', ingresos: 28000 },
  { dia: 'Sáb', ingresos: 8000 },
  { dia: 'Dom', ingresos: 0 },
];

const serviciosHoy = [
  { id: 1, hora: '9:00 AM', cliente: 'Bodegas Alianza', sucursal: 'Sucursal Norte', tipo: 'CCTV', tecnico: 'Carlos', direccion: 'Av. Industrial 2500, Col. Centro, Monterrey NL' },
  { id: 2, hora: '11:00 AM', cliente: 'Farma Plus', sucursal: 'Matriz', tipo: 'Red', tecnico: 'Miguel', direccion: 'Av. Insurgentes Sur 1234, Col. Del Valle, CDMX' },
  { id: 3, hora: '2:00 PM', cliente: 'Cliente nuevo', sucursal: 'Oficinas', tipo: 'Alarma', tecnico: 'Carlos', direccion: 'Paseo de la Reforma 500, Col. Juárez, CDMX' },
];

const mensajesMotivacionales = [
  'Cada día es una oportunidad para hacer crecer el negocio.',
  'La constancia supera al talento cuando el talento no es constante.',
  'Un buen servicio hoy es un cliente para siempre.',
  'Los detalles hacen la diferencia entre bueno y excelente.',
  'El equipo que trabaja unido, crece unido.',
  'La tecnología que instalamos protege lo que más importa.',
  'Hoy es un gran día para superar expectativas.',
];

const mapsNav = (dir) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dir)}`;

function getSaludo() {
  const h = new Date().getHours();
  if (h >= 6 && h < 12) return 'Buenos días';
  if (h >= 12 && h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

function getFechaHoy() {
  return new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function getMensajeDia() {
  const dow = new Date().getDay();
  return mensajesMotivacionales[dow] || mensajesMotivacionales[0];
}

function TooltipCustom({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', borderRadius: '10px', padding: '10px 14px', fontSize: '11px', fontFamily: PP, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', border: '1px solid #f0f0f0' }}>
      <div style={{ fontWeight: '700', marginBottom: '4px', color: colores.gris }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>${p.value.toLocaleString('es-MX', OPC_DINERO)}</div>
      ))}
    </div>
  );
}

const TAREAS_STORAGE_KEY = 'beep_tareas_personales';
const APROBACIONES_KEY = 'beep_aprobaciones';

const AGENTE_INFO = {
  beep:  { nombre: 'Beep',  icono: '🤖', color: '#5c9adb' },
  axel:  { nombre: 'Axel',  icono: '💼', color: '#5cd4b0' },
  max:   { nombre: 'Max',   icono: '⚙️', color: '#f4a261' },
  rex:   { nombre: 'Rex',   icono: '🏗️', color: '#00bcd4' },
  bruno: { nombre: 'Bruno', icono: '📦', color: '#9b59b6' },
  nova:  { nombre: 'Nova',  icono: '📋', color: '#e91e8c' },
  luna:  { nombre: 'Luna',  icono: '✨', color: '#dcde74' },
};

function seedAprobaciones() {
  const ahora = new Date().toISOString();
  return [
    {
      id: 1, folio: 'APR-2026-001', tipo: 'campana',
      titulo: 'Campaña de alarmas — Instagram y Facebook',
      descripcion: 'Luna propone campaña de 2 semanas para impulsar ventas de sistemas de alarma. Presupuesto estimado: $3,500. Alcance esperado: 15,000 personas.',
      agente: 'luna', prioridad: 'normal', estado: 'pendiente',
      datos: { presupuesto: 3500, duracion: '2 semanas', redes: ['Instagram', 'Facebook'], objetivo: 'Aumentar ventas de alarmas 20%' },
      fechaCreacion: ahora, fechaRespuesta: null, respondidoPor: null, notaRechazo: '',
    },
    {
      id: 2, folio: 'APR-2026-002', tipo: 'proyecto',
      titulo: 'Iniciar ejecución PRY-2026-100 — Bodegas Alianza',
      descripcion: 'Rex confirma que el anticipo de $97,016 fue recibido. Material preparado por Bruno. Técnicos asignados por Max. Listo para iniciar el lunes.',
      agente: 'rex', prioridad: 'urgente', estado: 'pendiente',
      datos: { folio: 'PRY-2026-100', cliente: 'Bodegas Alianza', monto: 121270, anticipo: 97016, tecnicosAsignados: ['Isaías Méndez'] },
      fechaCreacion: ahora, fechaRespuesta: null, respondidoPor: null, notaRechazo: '',
    },
    {
      id: 3, folio: 'APR-2026-003', tipo: 'gasto',
      titulo: 'Compra urgente de cable UTP — Stock bajo',
      descripcion: 'Bruno reporta stock crítico de Cable UTP Cat6 (quedan 500m, mínimo requerido: 100m). Solicita autorización para comprar 500m adicionales.',
      agente: 'bruno', prioridad: 'urgente', estado: 'pendiente',
      datos: { articulo: 'Cable UTP Cat6 exterior', cantidadActual: '500m', cantidadSolicita: '500m adicionales', costoEstimado: 11000 },
      fechaCreacion: ahora, fechaRespuesta: null, respondidoPor: null, notaRechazo: '',
    },
  ];
}

function cargarAprobaciones() {
  try {
    const raw = localStorage.getItem(APROBACIONES_KEY);
    if (!raw) {
      const seed = seedAprobaciones();
      localStorage.setItem(APROBACIONES_KEY, JSON.stringify(seed));
      return seed;
    }
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function cargarTareasPersonales() {
  try {
    const raw = localStorage.getItem(TAREAS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function MiDia({ nombre, rol, esCelular, onNavegar }) {
  // MI-DIA-V2: usuario actual desde context (necesario para Mi Bandeja).
  const { usuario } = useUsuario();
  const [width] = useState(window.innerWidth);
  const movil = esCelular || width < 768;
  const [tareasPersonales, setTareasPersonales] = useState(cargarTareasPersonales);
  const [pendientesAgenda, setPendientesAgenda] = useState([]);
  const [notasAgenda, setNotasAgenda] = useState([]);
  const [tareasBeep, setTareasBeep] = useState([]);
  const [aprobaciones, setAprobaciones] = useState(cargarAprobaciones);
  const [rechazandoId, setRechazandoId] = useState(null);
  const [notaRechazo, setNotaRechazo] = useState('');
  const [toast, setToast] = useState(null);
  const [pendientesExpo, setPendientesExpo] = useState([]);
  const [planMarketing, setPlanMarketing] = useState(null);
  const [publicacionesPlan, setPublicacionesPlan] = useState([]);
  const [generandoPlan, setGenerandoPlan] = useState(false);
  const [aprobacionesPlan, setAprobacionesPlan] = useState({});
  const [rechazandoPubIdx, setRechazandoPubIdx] = useState(null);
  const [notaRechazoPub, setNotaRechazoPub] = useState('');

  useEffect(() => {
    const sync = () => setAprobaciones(cargarAprobaciones());
    window.addEventListener('storage', sync);
    window.addEventListener('beep:aprobaciones-actualizadas', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('beep:aprobaciones-actualizadas', sync);
    };
  }, []);

  // Cargar pendientes de aprobación de Expo
  const API_EXPO = API_BASE;   // direccion del backend: config/api.js (fuente unica)
  useEffect(() => {
    const token = localStorage.getItem('beep_token');
    if (!token) return;
    if (!usuario || !usuario.esDueno) return;   // ENCAPSULAR-TUEXPO: para el resto /api/expo no existe (404)
    fetch(`${API_EXPO}/api/expo/usuarios-pendientes`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).then(r => r.json()).then(d => setPendientesExpo(d.pendientes || [])).catch(() => {});
  }, []);

  const aprobarExpo = async (aprobacionId) => {
    const token = localStorage.getItem('beep_token');
    try {
      await fetch(`${API_EXPO}/api/expo/aprobar-usuario/${aprobacionId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ aprobado_por: nombre }),
      });
      setPendientesExpo(prev => prev.filter(p => p.aprobacion_id !== aprobacionId));
      mostrarToast('✅ Acceso aprobado', 'verde');
    } catch { mostrarToast('Error', 'rojo'); }
  };

  // ============================================
  // Plan semanal de marketing (Luna)
  // ============================================
  const APROB_PLAN_KEY = 'beep_marketing_plan_aprobaciones';

  const mostrarPlanMarketing = rol === 'marketing' || rol === 'direccion' || rol === 'Marketing' || rol === 'Dirección' || rol === 'Administrador';

  useEffect(() => {
    if (!mostrarPlanMarketing) return;
    fetch(`${API_EXPO}/api/marketing/plan-semana`)
      .then(r => r.json())
      .then(d => {
        if (d.ok && d.plan) {
          setPlanMarketing(d.plan);
          const pubs = Array.isArray(d.plan.publicaciones) ? d.plan.publicaciones : (typeof d.plan.publicaciones === 'string' ? JSON.parse(d.plan.publicaciones) : []);
          setPublicacionesPlan(pubs);
          try {
            const raw = localStorage.getItem(`${APROB_PLAN_KEY}_${d.plan.id}`);
            if (raw) setAprobacionesPlan(JSON.parse(raw));
          } catch {}
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarPlanMarketing]);

  const persistirAprobacionesPlan = (nuevas) => {
    setAprobacionesPlan(nuevas);
    if (!planMarketing) return;
    try { localStorage.setItem(`${APROB_PLAN_KEY}_${planMarketing.id}`, JSON.stringify(nuevas)); } catch {}
  };

  const generarPlanMarketing = async () => {
    setGenerandoPlan(true);
    try {
      const r = await fetch(`${API_EXPO}/api/marketing/generar-plan`, { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
      const d = await r.json();
      if (d.ok) {
        setPlanMarketing(d.plan);
        const pubs = Array.isArray(d.publicaciones) ? d.publicaciones : [];
        setPublicacionesPlan(pubs);
        mostrarToast('✨ Plan generado por Luna', 'verde');
      } else {
        mostrarToast(d.error || 'Error generando plan', 'rojo');
      }
    } catch (e) {
      mostrarToast('Error de red', 'rojo');
    } finally {
      setGenerandoPlan(false);
    }
  };

  const aprobarPublicacion = (idx) => {
    persistirAprobacionesPlan({ ...aprobacionesPlan, [idx]: { estado: 'aprobado' } });
  };

  const iniciarRechazoPub = (idx) => {
    setRechazandoPubIdx(idx);
    setNotaRechazoPub(aprobacionesPlan[idx]?.motivo || '');
  };

  const confirmarRechazoPub = (idx) => {
    if (!notaRechazoPub.trim()) return;
    persistirAprobacionesPlan({ ...aprobacionesPlan, [idx]: { estado: 'rechazado', motivo: notaRechazoPub.trim() } });
    setRechazandoPubIdx(null);
    setNotaRechazoPub('');
  };

  const rechazarExpo = async (aprobacionId) => {
    const token = localStorage.getItem('beep_token');
    try {
      await fetch(`${API_EXPO}/api/expo/rechazar-usuario/${aprobacionId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setPendientesExpo(prev => prev.filter(p => p.aprobacion_id !== aprobacionId));
      mostrarToast('✗ Acceso rechazado', 'rojo');
    } catch { mostrarToast('Error', 'rojo'); }
  };

  const guardarAprobaciones = (arr) => {
    setAprobaciones(arr);
    try {
      localStorage.setItem(APROBACIONES_KEY, JSON.stringify(arr));
      window.dispatchEvent(new Event('beep:aprobaciones-actualizadas'));
    } catch {}
  };

  const mostrarToast = (mensaje, tipo) => {
    setToast({ mensaje, tipo });
    setTimeout(() => setToast(null), 2500);
  };

  const aprobarSolicitud = (id) => {
    const arr = aprobaciones.map(a => a.id === id ? {
      ...a, estado: 'aprobado', fechaRespuesta: new Date().toISOString(), respondidoPor: nombre || 'Ricardo',
    } : a);
    guardarAprobaciones(arr);
    mostrarToast('✅ Aprobado', 'verde');
  };

  const confirmarRechazo = (id) => {
    const arr = aprobaciones.map(a => a.id === id ? {
      ...a, estado: 'rechazado', fechaRespuesta: new Date().toISOString(), respondidoPor: nombre || 'Ricardo', notaRechazo: notaRechazo,
    } : a);
    guardarAprobaciones(arr);
    setRechazandoId(null);
    setNotaRechazo('');
    mostrarToast('✗ Rechazado', 'rojo');
  };

  const aprobacionesPendientes = aprobaciones.filter(a => a.estado === 'pendiente');
  const hayUrgentes = aprobacionesPendientes.some(a => a.prioridad === 'urgente');

  useEffect(() => {
    const cargar = () => {
      try {
        const arr = JSON.parse(localStorage.getItem('beep_tareas') || '[]');
        const hoy = new Date(); hoy.setHours(23, 59, 59, 999);
        const usuarios = JSON.parse(localStorage.getItem('beep_usuarios') || '[]');
        const yo = usuarios.find(u => (u.nombre || '').toLowerCase().includes((nombre || '').toLowerCase().split(' ')[0]));
        const yoId = yo?.id;
        const filtradas = arr.filter(t => {
          if (t.estado === 'completada' || t.estado === 'cancelada') return false;
          if (yoId && t.asignadoA !== yoId) return false;
          if (!t.fechaVencimiento) return false;
          return new Date(t.fechaVencimiento) <= hoy;
        });
        const peso = { alta: 3, media: 2, baja: 1 };
        filtradas.sort((a, b) => (peso[b.prioridad] || 0) - (peso[a.prioridad] || 0));
        setTareasBeep(filtradas.slice(0, 5));
      } catch {
        setTareasBeep([]);
      }
    };
    cargar();
    window.addEventListener('storage', cargar);
    return () => window.removeEventListener('storage', cargar);
  }, [nombre]);

  useEffect(() => {
    const cargarNotas = () => {
      try {
        const arr = JSON.parse(localStorage.getItem('beep_agenda_notas') || '[]');
        setNotasAgenda(arr.filter((n) => !n.archivada).slice(0, 5));
      } catch {
        setNotasAgenda([]);
      }
    };
    cargarNotas();
    window.addEventListener('storage', cargarNotas);
    return () => window.removeEventListener('storage', cargarNotas);
  }, []);

  const irAgenda = () => {
    if (onNavegar) onNavegar('requerimientos.agenda');
    else window.dispatchEvent(new CustomEvent('beep:navegar', { detail: 'requerimientos.agenda' }));
  };

  useEffect(() => {
    const cargar = () => {
      try {
        const arr = JSON.parse(localStorage.getItem('beep_agenda_pendientes') || '[]');
        setPendientesAgenda(arr.filter((p) => !p.hecha));
      } catch {
        setPendientesAgenda([]);
      }
    };
    cargar();
    window.addEventListener('beep:pendientes-actualizados', cargar);
    window.addEventListener('storage', cargar);
    return () => {
      window.removeEventListener('beep:pendientes-actualizados', cargar);
      window.removeEventListener('storage', cargar);
    };
  }, []);

  useEffect(() => {
    const sync = () => setTareasPersonales(cargarTareasPersonales());
    window.addEventListener('beep:tareas-actualizadas', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('beep:tareas-actualizadas', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const toggleTareaPersonal = (id) => {
    const actualizadas = tareasPersonales.map(t =>
      t.id === id ? { ...t, hecha: !t.hecha } : t
    );
    setTareasPersonales(actualizadas);
    try {
      localStorage.setItem(TAREAS_STORAGE_KEY, JSON.stringify(actualizadas));
      window.dispatchEvent(new Event('beep:tareas-actualizadas'));
    } catch {}
  };

  const tareasPendientes = tareasPersonales.filter(t => !t.hecha);

  const esAdmin = rol === 'Administrador' || rol === 'Dirección';
  const esTecnico = rol === 'Técnico';
  const esComercial = rol === 'Comercial';
  const esSupervisor = rol === 'Supervisor';
  const esRicardo = (nombre || '').toLowerCase().includes('ricardo');

  const tarjeta = { background: '#fff', borderRadius: '12px', padding: movil ? '14px' : '18px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' };
  const secTitulo = { fontSize: '14px', fontWeight: '600', color: colores.oscuro, marginBottom: '12px', fontFamily: PP };

  return (
    <div style={{ fontFamily: PP, display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.tipo === 'verde' ? colores.verde : colores.rojo,
          color: '#fff', padding: '12px 20px', borderRadius: 12,
          fontWeight: 700, fontSize: 14, boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
        }}>
          {toast.mensaje}
        </div>
      )}

      {/* ========== SALUDO ========== */}
      <div>
        <div style={{ fontSize: movil ? '22px' : '26px', fontWeight: '800', color: colores.gris, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>{getSaludo()}, {(nombre || 'Usuario').split(' ')[0]}</span>
          <span style={{ fontSize: 16 }}>Mi día ☀️</span>
          {aprobacionesPendientes.length > 0 && (
            <span style={{
              background: colores.rojo, color: '#fff',
              borderRadius: 999, padding: '2px 10px',
              fontSize: 13, fontWeight: 800,
              animation: 'pulse 1.6s ease-in-out infinite',
            }}>
              🔴{aprobacionesPendientes.length}
            </span>
          )}
        </div>
        <div style={{ fontSize: '13px', color: colores.grisClaro, marginTop: '4px' }}>{getFechaHoy()}</div>
        <div style={{ fontSize: '12px', color: colores.principal, marginTop: '6px', fontStyle: 'italic' }}>{getMensajeDia()}</div>
      </div>

      {/* ========== SALUD DEL CATALOGO DE SYSCOM (SYSCOM-REIMPORT 2026-09-21) ==========
           Va ARRIBA del todo a proposito: el barrido del catalogo estuvo muerto 24 dias
           y se descubrio por casualidad. El componente se oculta solo si no hay nada que
           avisar, y el backend solo se lo cuenta a direccion. */}
      <AvisoSincronizacionSyscom onNavegar={onNavegar} />

      {/* ========== VIGILANTE DE EXPEDIENTES (2026-09-23) ==========
           Lo primero que se ve al entrar, porque es lo que se estaba ignorando: 43
           expedientes comprando o facturando SIN PRECIO REGISTRADO. Se pliega solo si
           ya se vio hoy, y desaparece del todo si no hay nada que avisar. */}
      <TarjetaVigilante />
      {/* CONTRATISTAS-EXTERNOS Bloque 3: rechazos y 'sin respuesta' de cuadrillas externas, para quien agendo. */}
      <TarjetaAvisosExternos />

      {/* ========== MI BANDEJA (MI-DIA-V2) ========== */}
      {usuario?.id && (
        <MiBandeja esCelular={esCelular} onNavegar={onNavegar} />
      )}

      {/* ========== REPORTE MATUTINO DE BEEP ========== */}
      {(rol === 'direccion' || rol === 'Dirección' || esAdmin) && (
        <ReporteMatutino nombre={nombre} rol={rol} onNavegar={onNavegar} />
      )}

      {/* ========== PLAN DE MARKETING SEMANAL (LUNA) ========== */}
      {mostrarPlanMarketing && (
        <div style={{ background: '#fff', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: colores.oscuro }}>
              📣 Plan de marketing esta semana
            </div>
            {planMarketing && (
              <span style={{
                background: colores.amarillo, color: colores.gris,
                borderRadius: '12px', padding: '2px 10px',
                fontSize: '11px', fontWeight: '700',
              }}>
                ✨ Luna · {publicacionesPlan.length} pubs
              </span>
            )}
          </div>

          {!planMarketing ? (
            <div style={{ textAlign: 'center', padding: '16px 8px' }}>
              <div style={{ fontSize: 13, color: colores.grisClaro, marginBottom: 12 }}>
                Aún no hay plan semanal. Luna puede generarlo por ti.
              </div>
              <button
                type="button"
                onClick={generarPlanMarketing}
                disabled={generandoPlan}
                style={{
                  background: generandoPlan ? '#ccc' : colores.principal,
                  color: '#fff', border: 'none', borderRadius: 10,
                  padding: '10px 18px', fontSize: 13, fontWeight: 700,
                  cursor: generandoPlan ? 'not-allowed' : 'pointer', fontFamily: PP,
                }}
              >
                {generandoPlan ? 'Luna está pensando…' : '✨ Generar plan semanal'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: movil ? '1fr' : 'repeat(2, 1fr)', gap: 10 }}>
              {publicacionesPlan.map((p, idx) => {
                const emojiRed = ({
                  Instagram: '📷', instagram: '📷',
                  Facebook: '📘', facebook: '📘',
                  LinkedIn: '💼', linkedin: '💼',
                  TikTok: '🎵', tiktok: '🎵',
                })[p.red] || '📣';
                const aprob = aprobacionesPlan[idx];
                const enRechazo = rechazandoPubIdx === idx;
                const bg = aprob?.estado === 'aprobado' ? '#e8faf5'
                  : aprob?.estado === 'rechazado' ? '#fde8e8'
                  : colores.fondo;
                const borde = aprob?.estado === 'aprobado' ? colores.verde
                  : aprob?.estado === 'rechazado' ? colores.rojo
                  : colores.principal;
                const copyCorto = (p.copy || '').length > 120 ? (p.copy || '').slice(0, 117) + '…' : (p.copy || '');
                return (
                  <div key={idx} style={{
                    background: bg, borderRadius: 10, padding: '10px 12px',
                    borderLeft: `4px solid ${borde}`, display: 'flex', flexDirection: 'column', gap: 6,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 16 }}>{emojiRed}</span>
                      <span style={{
                        background: '#fff', color: colores.oscuro, borderRadius: 999,
                        padding: '1px 8px', fontSize: 10, fontWeight: 700,
                      }}>{p.dia}</span>
                      <span style={{
                        background: '#fff', color: colores.grisClaro, borderRadius: 999,
                        padding: '1px 8px', fontSize: 10, fontWeight: 700,
                      }}>{p.tipo}</span>
                      {aprob?.estado === 'aprobado' && (
                        <span style={{ marginLeft: 'auto', fontSize: 10, color: colores.verde, fontWeight: 800 }}>✓ Aprobada</span>
                      )}
                      {aprob?.estado === 'rechazado' && (
                        <span style={{ marginLeft: 'auto', fontSize: 10, color: colores.rojo, fontWeight: 800 }}>✗ Rechazada</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: colores.gris, lineHeight: 1.2 }}>
                      {p.titulo}
                    </div>
                    <div style={{ fontSize: 12, color: colores.grisClaro, lineHeight: 1.35 }}>
                      {copyCorto}
                    </div>
                    {p.hashtags && (
                      <div style={{ fontSize: 10, color: colores.principal, fontWeight: 600 }}>
                        {p.hashtags}
                      </div>
                    )}
                    {aprob?.estado === 'rechazado' && aprob.motivo && (
                      <div style={{ fontSize: 11, color: colores.rojo, fontStyle: 'italic' }}>
                        Motivo: {aprob.motivo}
                      </div>
                    )}
                    {enRechazo ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                        <input
                          type="text"
                          autoFocus
                          value={notaRechazoPub}
                          onChange={(e) => setNotaRechazoPub(e.target.value)}
                          placeholder="¿Por qué la rechazas?"
                          style={{
                            padding: '6px 10px', borderRadius: 8,
                            border: `1px solid ${colores.rojo}66`,
                            fontSize: 12, fontFamily: PP, outline: 'none',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => confirmarRechazoPub(idx)}
                            disabled={!notaRechazoPub.trim()}
                            style={{
                              flex: 1, background: colores.rojo, color: '#fff',
                              border: 'none', borderRadius: 6, padding: '6px 8px',
                              fontSize: 11, fontWeight: 700, fontFamily: PP,
                              cursor: notaRechazoPub.trim() ? 'pointer' : 'not-allowed',
                              opacity: notaRechazoPub.trim() ? 1 : 0.5,
                            }}
                          >Confirmar</button>
                          <button
                            type="button"
                            onClick={() => { setRechazandoPubIdx(null); setNotaRechazoPub(''); }}
                            style={{
                              background: 'transparent', color: colores.grisClaro,
                              border: `1px solid ${colores.grisClaro}66`,
                              borderRadius: 6, padding: '6px 10px',
                              fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
                            }}
                          >Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                        <button
                          type="button"
                          onClick={() => aprobarPublicacion(idx)}
                          style={{
                            flex: 1, background: aprob?.estado === 'aprobado' ? colores.verde : '#fff',
                            color: aprob?.estado === 'aprobado' ? '#fff' : colores.verde,
                            border: `1.5px solid ${colores.verde}`,
                            borderRadius: 6, padding: '6px 8px',
                            fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
                          }}
                        >✅ Aprobar</button>
                        <button
                          type="button"
                          onClick={() => iniciarRechazoPub(idx)}
                          style={{
                            flex: 1, background: aprob?.estado === 'rechazado' ? colores.rojo : '#fff',
                            color: aprob?.estado === 'rechazado' ? '#fff' : colores.rojo,
                            border: `1.5px solid ${colores.rojo}`,
                            borderRadius: 6, padding: '6px 8px',
                            fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
                          }}
                        >❌ Rechazar</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========== APROBACIONES PENDIENTES ========== */}
      {esRicardo && aprobacionesPendientes.length > 0 && (
        <div style={{
          background: hayUrgentes ? '#fdecec' : '#eaf3fb',
          borderRadius: 14,
          padding: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          border: `1px solid ${hayUrgentes ? '#f4c4c4' : '#cfe0f0'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: colores.oscuro }}>
              ⚡ Requiere tu aprobación
            </div>
            <span style={{
              background: hayUrgentes ? colores.rojo : colores.principal,
              color: '#fff', borderRadius: 999, padding: '3px 12px',
              fontSize: 11, fontWeight: 700,
            }}>
              {aprobacionesPendientes.length} {aprobacionesPendientes.length === 1 ? 'solicitud pendiente' : 'solicitudes pendientes'}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {aprobacionesPendientes.map(a => {
              const ag = AGENTE_INFO[a.agente] || AGENTE_INFO.beep;
              const enRechazo = rechazandoId === a.id;
              return (
                <div key={a.id} style={{
                  background: '#fff', borderRadius: 12, padding: '12px 14px',
                  borderLeft: `5px solid ${ag.color}`,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{
                      background: ag.color + '22', color: colores.gris,
                      borderRadius: 999, padding: '2px 10px',
                      fontSize: 11, fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
                      {ag.icono} {ag.nombre}
                    </span>
                    {a.prioridad === 'urgente' && (
                      <span style={{
                        background: colores.rojo, color: '#fff',
                        borderRadius: 999, padding: '2px 10px',
                        fontSize: 10, fontWeight: 800,
                        animation: 'pulse 1.4s ease-in-out infinite',
                      }}>
                        ⚡ URGENTE
                      </span>
                    )}
                    <span style={{ fontSize: 10, color: colores.grisClaro, fontWeight: 700, marginLeft: 'auto' }}>
                      {a.folio}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: colores.gris, marginBottom: 4 }}>
                    {a.titulo}
                  </div>
                  <div style={{
                    fontSize: 12, color: colores.grisClaro, marginBottom: 10,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {a.descripcion}
                  </div>
                  {enRechazo ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input
                        type="text"
                        autoFocus
                        value={notaRechazo}
                        onChange={(e) => setNotaRechazo(e.target.value)}
                        placeholder="¿Por qué lo rechazas?"
                        style={{
                          padding: '8px 12px', borderRadius: 8,
                          border: `1px solid ${colores.rojo}66`,
                          fontSize: 13, fontFamily: PP, outline: 'none',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => confirmarRechazo(a.id)}
                          disabled={!notaRechazo.trim()}
                          style={{
                            flex: 1, background: colores.rojo, color: '#fff',
                            border: 'none', borderRadius: 8, padding: '8px 12px',
                            fontSize: 12, fontWeight: 700,
                            cursor: notaRechazo.trim() ? 'pointer' : 'not-allowed',
                            opacity: notaRechazo.trim() ? 1 : 0.5,
                          }}
                        >
                          Confirmar rechazo
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRechazandoId(null); setNotaRechazo(''); }}
                          style={{
                            background: 'transparent', color: colores.grisClaro,
                            border: `1px solid ${colores.grisClaro}66`,
                            borderRadius: 8, padding: '8px 12px',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => aprobarSolicitud(a.id)}
                        style={{
                          flex: 1, background: colores.verde, color: '#fff',
                          border: 'none', borderRadius: 8, padding: '8px 12px',
                          fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        ✓ Aprobar
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRechazandoId(a.id); setNotaRechazo(''); }}
                        style={{
                          flex: 1, background: '#fff', color: colores.rojo,
                          border: `1.5px solid ${colores.rojo}`,
                          borderRadius: 8, padding: '8px 12px',
                          fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        ✗ Rechazar
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }`}</style>
        </div>
      )}

      {/* ========== APROBACIONES EXPO ========== */}
      {pendientesExpo.length > 0 && (
        <div style={{
          background: '#f5f0f7', borderRadius: 14, padding: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          border: '1px solid rgba(103,35,105,0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#270f2b' }}>
              🎪 Accesos TuExpo
            </div>
            <span style={{
              background: '#672369', color: '#fff', borderRadius: 999, padding: '3px 12px',
              fontSize: 11, fontWeight: 700,
            }}>
              {pendientesExpo.length} pendientes
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pendientesExpo.map(p => (
              <div key={p.aprobacion_id} style={{
                background: '#fff', borderRadius: 12, padding: '12px 14px',
                borderLeft: '5px solid #94b803',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{
                    background: '#94b80322', color: '#270f2b',
                    borderRadius: 999, padding: '2px 10px',
                    fontSize: 11, fontWeight: 700,
                  }}>🎪 Nuevo técnico</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#333', marginBottom: 2 }}>
                  {p.nombre}
                </div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                  {p.empresa} · Quiere acceder al portal de montaje
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => aprobarExpo(p.aprobacion_id)} style={{
                    flex: 1, background: '#94b803', color: '#270f2b',
                    border: 'none', borderRadius: 8, padding: '8px 12px',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
                  }}>✓ Aprobar acceso</button>
                  <button onClick={() => rechazarExpo(p.aprobacion_id)} style={{
                    flex: 1, background: '#fff', color: colores.rojo,
                    border: `1.5px solid ${colores.rojo}`,
                    borderRadius: 8, padding: '8px 12px',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
                  }}>✗ Rechazar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========== MIS TAREAS DE HOY ========== */}
      {tareasBeep.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: colores.oscuro }}>
              ✅ Mis tareas de hoy
            </div>
            <span style={{
              background: colores.rojo, color: '#fff',
              borderRadius: '12px', padding: '2px 10px',
              fontSize: '11px', fontWeight: '700',
            }}>
              {tareasBeep.length}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {tareasBeep.map(t => {
              const colorPrio = t.prioridad === 'alta' ? '#e05c5c' : t.prioridad === 'media' ? '#dcde74' : '#5cd4b0';
              return (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 10px', background: colores.fondo, borderRadius: '10px',
                  borderLeft: `4px solid ${colorPrio}`,
                }}>
                  <span style={{ flex: 1, fontSize: '13px', color: colores.gris, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.titulo}
                  </span>
                  <span style={{ fontSize: 10, color: colores.grisClaro, fontWeight: 700 }}>
                    {t.folio}
                  </span>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => onNavegar && onNavegar('inicio.tareas')}
            style={{
              marginTop: 10,
              background: colores.principal,
              border: 'none',
              color: '#fff',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Ver todas mis tareas
          </button>
        </div>
      )}

      {/* ========== MIS NOTAS RECIENTES ========== */}
      {notasAgenda.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: colores.oscuro }}>
              📓 Mis notas recientes
            </div>
            <span style={{
              background: colores.amarillo, color: colores.gris,
              borderRadius: '12px', padding: '2px 10px',
              fontSize: '11px', fontWeight: '700',
            }}>
              {notasAgenda.length}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {notasAgenda.map(n => (
              <div key={n.id} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 10px', background: colores.fondo, borderRadius: '10px',
              }}>
                <span style={{ fontSize: 14 }}>📝</span>
                <span style={{ flex: 1, fontSize: '13px', color: colores.gris, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {n.titulo || (n.contenido || '').slice(0, 60) || 'Sin título'}
                </span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              onClick={irAgenda}
              style={{
                background: colores.principal,
                border: 'none',
                color: '#fff',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + Nueva nota rápida
            </button>
            <button
              type="button"
              onClick={irAgenda}
              style={{
                background: 'transparent',
                border: `1px solid ${colores.principal}77`,
                color: colores.oscuro,
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Ver todas
            </button>
          </div>
        </div>
      )}

      {/* ========== MIS PENDIENTES (AGENDA) ========== */}
      <div style={{ background: '#fff', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: colores.oscuro }}>
            📋 Mis pendientes
          </div>
          {pendientesAgenda.length > 0 && (
            <span style={{
              background: colores.principal, color: '#fff',
              borderRadius: '12px', padding: '2px 10px',
              fontSize: '11px', fontWeight: '700',
            }}>
              {pendientesAgenda.length}
            </span>
          )}
        </div>
        {pendientesAgenda.length === 0 ? (
          <div style={{ fontSize: '13px', color: colores.grisClaro, padding: '8px 0' }}>
            No tienes pendientes activos 🎉
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {pendientesAgenda.slice(0, 5).map(p => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 10px', background: colores.fondo, borderRadius: '10px',
                }}>
                  <span style={{ fontSize: 14 }}>•</span>
                  <span style={{ flex: 1, fontSize: '13px', color: colores.gris }}>{p.texto}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('beep:navegar', { detail: 'requerimientos.agenda' }))}
              style={{
                marginTop: 10,
                background: 'transparent',
                border: `1px solid ${colores.principal}77`,
                color: colores.oscuro,
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Ver todos
            </button>
          </>
        )}
      </div>

      {/* ========== MIS TAREAS PENDIENTES ========== */}
      <div style={{ background: '#fff', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: colores.oscuro }}>
            📝 Mis tareas pendientes
          </div>
          {tareasPendientes.length > 0 && (
            <span style={{
              background: colores.rojo, color: '#fff',
              borderRadius: '12px', padding: '2px 10px',
              fontSize: '11px', fontWeight: '700',
            }}>
              {tareasPendientes.length}
            </span>
          )}
        </div>
        {tareasPendientes.length === 0 ? (
          <div style={{ fontSize: '13px', color: colores.grisClaro, padding: '8px 0' }}>
            No tienes tareas pendientes para hoy 🎉
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {tareasPendientes.map(t => (
              <div key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 10px', background: colores.fondo, borderRadius: '10px',
              }}>
                <input
                  type="checkbox"
                  checked={t.hecha}
                  onChange={() => toggleTareaPersonal(t.id)}
                  style={{ cursor: 'pointer', accentColor: colores.verde }}
                />
                <span style={{ flex: 1, fontSize: '13px', color: colores.gris }}>{t.texto}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ========== PIPELINE DE COTIZACIONES ========== */}
      {/* CO-1.1: oculto widget de cotizaciones mock por no estar conectado a
          API real. En CO-5+ se reemplazara por un widget de cotizaciones
          reales vinculadas a expedientes. Bloque preservado: para reactivar,
          eliminar el `false &&` de abajo. */}
      {false && (
        <div style={{ background: '#fff', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <PipelineCotizaciones
            rol={rol}
            nombre={nombre}
            compacto={true}
            onNavegar={onNavegar}
          />
        </div>
      )}

      {/* ==================== DIRECCIÓN / ADMINISTRADOR ==================== */}
      {esAdmin && (
        <>
          {/* A — PENDIENTES POR AUTORIZAR */}
          <div style={tarjeta}>
            <div style={secTitulo}>Pendientes por autorizar</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {esRicardo && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#fde8e8', borderRadius: '10px', borderLeft: `4px solid ${colores.rojo}` }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris }}>Contenido de marketing</div>
                    <div style={{ fontSize: '11px', color: colores.grisClaro }}>Publicaciones pendientes de aprobación</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px', fontWeight: '800', color: colores.rojo }}>0</span>
                    <button style={{ padding: '5px 12px', background: colores.rojo, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>Revisar</button>
                  </div>
                </div>
              )}
              {[
                { label: 'Cotizaciones mayores a $50,000', desc: 'Requieren autorización de Dirección', valor: 0 },
                { label: 'Proyectos nuevos', desc: 'Pendientes de aprobación', valor: 0 },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#fde8e8', borderRadius: '10px', borderLeft: `4px solid ${colores.rojo}` }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris }}>{item.label}</div>
                    <div style={{ fontSize: '11px', color: colores.grisClaro }}>{item.desc}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px', fontWeight: '800', color: colores.rojo }}>{item.valor}</span>
                    <button style={{ padding: '5px 12px', background: colores.rojo, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>Revisar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* B — KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: movil ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '12px' }}>
            {[
              { icon: '💰', label: 'Ingresos del día', valor: '$0.00', tendencia: null, bg: '#e8faf5' },
              { icon: '📋', label: 'Proyectos activos', valor: '3', tendencia: null, bg: '#e6f1fb' },
              { icon: '👷', label: 'Técnicos en campo', valor: '2', tendencia: null, bg: '#fffbe6' },
              { icon: '🎯', label: 'Leads nuevos', valor: '1', tendencia: '+1', bg: '#f3effe' },
            ].map((k, i) => (
              <div key={i} style={{ ...tarjeta, background: k.bg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '16px' }}>{k.icon}</span>
                  <span style={{ fontSize: '11px', color: colores.grisClaro, fontWeight: '500' }}>{k.label}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: colores.gris, lineHeight: 1 }}>{k.valor}</div>
                  {k.tendencia && <span style={{ fontSize: '12px', fontWeight: '600', color: '#1D9E75' }}>{k.tendencia}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* C — ALERTAS */}
          <div style={{ display: 'grid', gridTemplateColumns: movil ? '1fr' : 'repeat(3, 1fr)', gap: '12px' }}>
            {[
              { label: 'Proyectos retrasados', valor: 0, color: colores.rojo },
              { label: 'Facturas vencidas', valor: 0, color: '#9a8300' },
              { label: 'Técnicos sin actividad hoy', valor: 0, color: colores.grisClaro },
            ].map((a, i) => (
              <div key={i} style={{ ...tarjeta, borderLeft: `4px solid ${a.color}`, background: a.valor > 0 ? '#fffbe6' : '#fff' }}>
                <div style={{ fontSize: '11px', color: a.color, fontWeight: '500', marginBottom: '4px' }}>{'⚠️'} {a.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: a.valor > 0 ? a.color : colores.gris }}>{a.valor}</div>
              </div>
            ))}
          </div>

          {/* D — SERVICIOS DE HOY */}
          <div style={tarjeta}>
            <div style={secTitulo}>Servicios de hoy</div>
            {serviciosHoy.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {serviciosHoy.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: colores.fondo, borderRadius: '10px' }}>
                    <div style={{ minWidth: '65px', fontSize: '13px', fontWeight: '700', color: colores.oscuro }}>{s.hora}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.cliente} — {s.sucursal}</div>
                      <div style={{ fontSize: '11px', color: colores.grisClaro }}>{s.tipo} — {s.tecnico} (Técnico)</div>
                    </div>
                    <a href={mapsNav(s.direccion)} target="_blank" rel="noopener noreferrer" style={{ padding: '6px 10px', background: colores.oscuro, color: '#fff', borderRadius: '8px', fontSize: '11px', fontWeight: '600', textDecoration: 'none', flexShrink: 0 }}>{'🚗'} Ir</a>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', fontSize: '13px', color: colores.grisClaro }}>No hay servicios programados para hoy</div>
            )}
          </div>

          {/* E — RESUMEN COMERCIAL */}
          <div style={tarjeta}>
            <div style={secTitulo}>Resumen comercial</div>
            <div style={{ display: 'grid', gridTemplateColumns: movil ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '12px' }}>
              {[
                { label: 'Leads nuevos hoy', valor: 1, color: colores.principal },
                { label: 'Cotizaciones esta semana', valor: 2, color: '#f4a261' },
                { label: 'Conversiones este mes', valor: 0, color: '#1D9E75' },
                { label: 'Contratos activos', valor: 5, color: colores.oscuro },
              ].map((m, i) => (
                <div key={i} style={{ textAlign: 'center', padding: '10px', background: colores.fondo, borderRadius: '10px' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: m.color }}>{m.valor}</div>
                  <div style={{ fontSize: '10px', color: colores.grisClaro, marginTop: '2px' }}>{m.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* F — GRÁFICA */}
          <div style={tarjeta}>
            <div style={secTitulo}>Ingresos esta semana</div>
            <div style={{ height: movil ? '180px' : '220px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={graficaSemana} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="dia" tick={{ fontSize: 11, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: colores.grisClaro, fontFamily: 'Poppins' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? '$' + (v / 1000) + 'k' : '$' + v} width={42} />
                  <Tooltip content={<TooltipCustom />} />
                  <Bar dataKey="ingresos" name="Ingresos" fill={colores.principal} radius={[6, 6, 0, 0]} animationDuration={1200} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* ==================== TÉCNICO ==================== */}
      {esTecnico && (
        <>
          <div style={tarjeta}>
            <div style={secTitulo}>Mis servicios de hoy</div>
            {serviciosHoy.slice(0, 2).map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: colores.fondo, borderRadius: '10px', marginBottom: '8px' }}>
                <div style={{ minWidth: '60px', fontSize: '15px', fontWeight: '800', color: colores.oscuro }}>{s.hora}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris }}>{s.cliente}</div>
                  <div style={{ fontSize: '12px', color: colores.grisClaro }}>{s.sucursal} — {s.tipo}</div>
                  <div style={{ fontSize: '11px', color: colores.grisClaro, marginTop: '2px' }}>{s.direccion}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                  <a href={mapsNav(s.direccion)} target="_blank" rel="noopener noreferrer" style={{ padding: '8px 12px', background: colores.oscuro, color: '#fff', borderRadius: '8px', fontSize: '12px', fontWeight: '700', textDecoration: 'none', textAlign: 'center' }}>{'🚗'} Ir</a>
                  <button style={{ padding: '8px 12px', background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>Iniciar servicio</button>
                </div>
              </div>
            ))}
          </div>
          <div style={tarjeta}>
            <div style={secTitulo}>Materiales que necesitas</div>
            {['8 cámaras Hikvision DS-2CD2145', '200m cable UTP Cat6', '1 NVR 16CH Hikvision', 'Kit de herramientas básico', 'Conectores RJ45 x50'].map((m, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '13px', color: colores.gris }}>
                <span style={{ color: colores.principal }}>{'•'}</span> {m}
              </div>
            ))}
          </div>
          <div style={tarjeta}>
            <div style={secTitulo}>Tareas pendientes</div>
            {['Documentar equipo instalado en Bodegas Alianza', 'Subir fotos de instalación — Farma Plus', 'Devolver taladro Dewalt al almacén'].map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '13px', color: colores.gris }}>
                <span style={{ color: colores.rojo }}>{'○'}</span> {t}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ==================== COMERCIAL ==================== */}
      {esComercial && (
        <>
          <div style={tarjeta}>
            <div style={secTitulo}>Leads asignados — pendientes de contactar</div>
            {['Roberto Sánchez — Bodega Central SA — WhatsApp — Interesado en CCTV', 'Ana López — Clínica Vida — Web — Control de acceso', 'Luis Morales — Restaurante El Patio — Referido — Redes'].map((l, i) => (
              <div key={i} style={{ padding: '10px 12px', background: colores.fondo, borderRadius: '8px', marginBottom: '6px', fontSize: '13px', color: colores.gris, borderLeft: `3px solid ${colores.amarillo}` }}>{l}</div>
            ))}
          </div>
          <div style={tarjeta}>
            <div style={secTitulo}>Cotizaciones pendientes de enviar</div>
            {['COT-2026-003 — Constructora del Valle — $0 — Borrador', 'COT-2026-005 — Farma Plus — $32,480 — Vista por cliente'].map((c, i) => (
              <div key={i} style={{ padding: '10px 12px', background: colores.fondo, borderRadius: '8px', marginBottom: '6px', fontSize: '13px', color: colores.gris, borderLeft: `3px solid ${colores.principal}` }}>{c}</div>
            ))}
          </div>
          <div style={tarjeta}>
            <div style={secTitulo}>Clientes por dar seguimiento hoy</div>
            {['Patricia Ruiz — Colegio Montessori — Seguimiento cotización enviada', 'Fernando Castro — Castro Corp — Revisión de contrato de mantenimiento'].map((c, i) => (
              <div key={i} style={{ padding: '10px 12px', background: colores.fondo, borderRadius: '8px', marginBottom: '6px', fontSize: '13px', color: colores.gris, borderLeft: `3px solid ${colores.verde}` }}>{c}</div>
            ))}
          </div>
          <div style={tarjeta}>
            <div style={secTitulo}>Meta del mes</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '24px', fontWeight: '700', color: colores.gris }}>$32,480</span>
              <span style={{ fontSize: '13px', color: colores.grisClaro }}>/ $200,000</span>
            </div>
            <div style={{ height: '10px', background: '#e8e8e8', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: '16%', background: colores.principal, borderRadius: '5px', transition: 'width 0.8s ease' }} />
            </div>
            <div style={{ fontSize: '11px', color: colores.grisClaro, marginTop: '4px' }}>16% de la meta mensual</div>
          </div>
        </>
      )}

      {/* ==================== SUPERVISOR ==================== */}
      {esSupervisor && (
        <>
          <div style={tarjeta}>
            <div style={secTitulo}>Estado de técnicos a mi cargo</div>
            {[
              { nombre: 'Carlos', estatus: 'en-campo', servicio: 'Bodegas Alianza — CCTV', color: '#1D9E75', emoji: '🟢' },
              { nombre: 'Miguel', estatus: 'en-campo', servicio: 'Farma Plus — Red', color: '#1D9E75', emoji: '🟢' },
              { nombre: 'Pedro', estatus: 'en-oficina', servicio: 'Sin servicio asignado', color: '#9a8300', emoji: '🟡' },
              { nombre: 'Omar', estatus: 'no-reportado', servicio: 'No se ha reportado hoy', color: colores.rojo, emoji: '🔴' },
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: '16px' }}>{t.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris }}>{t.nombre}</div>
                  <div style={{ fontSize: '11px', color: colores.grisClaro }}>{t.servicio}</div>
                </div>
                <span style={{ fontSize: '11px', fontWeight: '600', color: t.color }}>{t.estatus.replace('-', ' ')}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {[
              { label: 'En curso', valor: 2, color: '#1D9E75' },
              { label: 'Completados hoy', valor: 0, color: colores.principal },
              { label: 'Pendientes', valor: 1, color: '#9a8300' },
            ].map((k, i) => (
              <div key={i} style={{ ...tarjeta, textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: k.color }}>{k.valor}</div>
                <div style={{ fontSize: '10px', color: colores.grisClaro, marginTop: '2px' }}>{k.label}</div>
              </div>
            ))}
          </div>
          <div style={tarjeta}>
            <div style={secTitulo}>Servicios de hoy</div>
            {serviciosHoy.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', background: colores.fondo, borderRadius: '10px', marginBottom: '6px' }}>
                <div style={{ minWidth: '60px', fontSize: '13px', fontWeight: '700', color: colores.oscuro }}>{s.hora}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris }}>{s.cliente}</div>
                  <div style={{ fontSize: '11px', color: colores.grisClaro }}>{s.tipo} — {s.tecnico}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default MiDia;
